/**
 * AIReasoningModal — displays AI reasoning after queue optimization.
 * Shows: reasoning summary, key decisions, concerns, and quick wins.
 */

import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

export default function AIReasoningModal({ open, onClose, result }) {
  if (!result) return null;

  const {
    ai_reasoning,
    key_decisions = [],
    concerns = [],
    quick_wins = [],
    jobs = [],
    used_ai = true,
    message,
  } = result;

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <Dialog.Title className="text-lg font-semibold text-white">
                        {used_ai ? 'AI Queue Optimization' : 'Queue Optimization'}
                      </Dialog.Title>
                    </div>
                    <button
                      onClick={onClose}
                      className="text-white/80 hover:text-white p-1 rounded-md hover:bg-white/10"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {message && (
                    <p className="text-sm text-white/80 mt-1">{message}</p>
                  )}
                </div>

                <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
                  {/* AI Reasoning */}
                  {ai_reasoning && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Analysis
                      </h3>
                      <div className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {ai_reasoning}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Key Decisions */}
                  {key_decisions.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                        Key Decisions
                      </h3>
                      <div className="space-y-2">
                        {key_decisions.map((decision, idx) => (
                          <div
                            key={idx}
                            className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-3"
                          >
                            <span className="text-xs font-bold bg-blue-600 text-white px-1.5 py-0.5 rounded">
                              {decision.job}
                            </span>
                            <div className="flex-1">
                              <span className="text-xs font-medium text-blue-700 uppercase">
                                {decision.action}
                              </span>
                              <p className="text-sm text-gray-700 mt-0.5">{decision.reason}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quick Wins */}
                  {quick_wins.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Quick Wins
                      </h3>
                      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                        <ul className="space-y-1">
                          {quick_wins.map((win, idx) => (
                            <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                              <span className="text-emerald-500 mt-0.5">✓</span>
                              {win}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Concerns */}
                  {concerns.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        Concerns
                      </h3>
                      <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                        <ul className="space-y-1">
                          {concerns.map((concern, idx) => (
                            <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                              <span className="text-amber-500 mt-0.5">⚠</span>
                              {concern}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Jobs Summary */}
                  {jobs.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        Queue Order ({jobs.length} jobs)
                      </h3>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-gray-500">#</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500">Job</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500">Customer</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500">Tier</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500">Change</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {jobs.slice(0, 10).map((job) => {
                              const change = (job.old_position || 999) - job.new_position;
                              return (
                                <tr key={job.job_number} className={job.ai_decision ? 'bg-violet-50' : ''}>
                                  <td className="px-3 py-1.5 font-medium text-gray-800">{job.new_position}</td>
                                  <td className="px-3 py-1.5 font-mono text-gray-700">{job.job_number}</td>
                                  <td className="px-3 py-1.5 text-gray-600 max-w-[120px] truncate">{job.customer}</td>
                                  <td className="px-3 py-1.5">
                                    <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px]">
                                      {job.tier_name}
                                    </span>
                                  </td>
                                  <td className="px-3 py-1.5">
                                    {job.is_pinned ? (
                                      <span className="text-gray-400">📌</span>
                                    ) : change > 0 ? (
                                      <span className="text-emerald-600">↑{change}</span>
                                    ) : change < 0 ? (
                                      <span className="text-red-500">↓{Math.abs(change)}</span>
                                    ) : (
                                      <span className="text-gray-400">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {jobs.length > 10 && (
                          <div className="bg-gray-50 px-3 py-2 text-xs text-gray-500 text-center">
                            +{jobs.length - 10} more jobs
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-100 px-5 py-3 flex justify-end">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
